import React from 'react'
import { ChevronRight } from 'lucide-react'
import { ContentSection, FaqItem } from '@/types'

interface ArticleBodyProps {
  /** Giriş paragrafı — sayfanın ilk ekranında görünen özet. */
  intro?: string
  sections: ContentSection[]
  faqs?: FaqItem[]
  /** Sayfa altındaki tıbbi bilgilendirme notu. */
  disclaimer?: string
}

/**
 * Rehber ve hizmet sayfalarının gövdesini render eder.
 * Tüm uzun içerik sayfaları bunu kullanır; böylece başlık hiyerarşisi,
 * tablo biçimi ve SSS görünümü site genelinde aynı kalır.
 */
const ArticleBody: React.FC<ArticleBodyProps> = ({ intro, sections, faqs, disclaimer }) => (
  <>
    {intro && (
      <p className="text-lg text-gray-700 leading-relaxed mb-8 pb-8 border-b border-gray-200">
        {intro}
      </p>
    )}

    <div className="space-y-10">
      {sections.map(sec => (
        <div key={sec.heading}>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{sec.heading}</h2>

          {sec.paragraphs?.map((p, i) => (
            <p key={i} className="text-gray-700 leading-relaxed mb-3">{p}</p>
          ))}

          {sec.bullets && (
            <ul className="space-y-2 mt-3">
              {sec.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700 leading-relaxed">
                  <ChevronRight className="w-4 h-4 text-primary-500 mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {sec.table && (
            <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                {sec.table.caption && (
                  <caption className="text-left text-xs text-gray-500 px-4 py-2 bg-gray-50 border-b">
                    {sec.table.caption}
                  </caption>
                )}
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    {sec.table.columns.map(c => (
                      <th key={c} className="text-left font-semibold px-4 py-3">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sec.table.rows.map((row, ri) => (
                    <tr key={ri} className="hover:bg-primary-50/40 transition-colors">
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          className={`px-4 py-3 align-top ${
                            ci === 0 ? 'font-medium text-gray-900 whitespace-nowrap' : 'text-gray-700'
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>

    {faqs && faqs.length > 0 && (
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-5">Sık sorulan sorular</h2>
        <div className="space-y-3">
          {faqs.map(f => (
            <details key={f.question} className="group rounded-xl border border-gray-200 bg-white overflow-hidden">
              <summary className="cursor-pointer list-none px-5 py-4 font-semibold text-gray-900 flex items-center justify-between gap-4 hover:bg-gray-50">
                {f.question}
                <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="px-5 pb-4 text-gray-700 leading-relaxed">{f.answer}</div>
            </details>
          ))}
        </div>
      </div>
    )}

    {disclaimer && (
      <div className="mt-10 p-5 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-600 leading-relaxed">
        {disclaimer}
      </div>
    )}
  </>
)

export default ArticleBody
